import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, Check, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewsletterForm = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      toast.error(t("footer.newsletterInvalid"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, {
        email: value,
        lang,
        source: "footer",
        interest: "consultation",
      });
      if (data.already_subscribed) {
        toast.success(t("footer.newsletterExists"));
      } else {
        toast.success(t("footer.newsletterSuccess"));
      }
      setEmail("");
      setDone(true);
      setTimeout(() => setDone(false), 4500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("footer.newsletterError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="text-center max-w-2xl mx-auto" data-testid="newsletter-block">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#E6A95B]/40 bg-[#E6A95B]/10 mb-5">
        <Sparkles className="w-3.5 h-3.5 text-[#E6A95B]" />
        <span className="text-[#E6A95B] text-[11px] uppercase tracking-[0.24em] font-bold">
          {lang === "ar" ? "عرض مجاني" : "Free Offer"}
        </span>
      </div>
      <h3 className="font-display text-2xl md:text-3xl lg:text-4xl text-white leading-tight mb-4">
        {t("footer.newsletterTitle")}
      </h3>
      <p className="text-white/65 text-sm md:text-base leading-relaxed mb-7 max-w-xl mx-auto">
        {t("footer.newsletterSub")}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-stretch gap-2 max-w-lg mx-auto" noValidate>
        <div className="relative flex-1">
          <Mail className="w-4 h-4 text-[#E6A95B] absolute top-1/2 -translate-y-1/2 start-3.5 pointer-events-none" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("footer.newsletterPlaceholder")}
            data-testid="newsletter-email-input"
            className="w-full h-11 rounded-full bg-black/50 border border-white/15 ps-10 pe-4 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[#E6A95B] focus:ring-1 focus:ring-[#E6A95B]/40 transition"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="newsletter-submit-btn"
          className="h-11 px-5 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold text-sm hover:brightness-110 hover:shadow-[0_0_30px_-5px_rgba(230,169,91,0.7)] disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-all"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : done ? (
            <Check className="w-4 h-4" />
          ) : null}
          {t("footer.newsletterCta")}
        </button>
      </form>
    </div>
  );
};

export default NewsletterForm;
